import React, { useState } from 'react';
import styles from '../LayoutDetail.module.css';
import { setBlinkingSchedule, getBlinkingSchedule } from '../../../api/layoutApi';
import { changeActiveLine } from '../../../api/lineApi';
import { Alert } from '@mui/material';
import { Snackbar } from '@mui/material';

const DetailTab = ({ 
    lines, 
    setLines,
    loading, 
    layoutId,
    selectedLine,
    onLineSelect,
    onAddLine, 
    onEditLine, 
    onDeleteLine, 
    onSignalChange,
    onAllBlinking
}) => {
    const [blinkingSchedule, setBlinkingScheduleState] = useState({
        from: { hh: 0, mm: 0},
        to: { hh: 0, mm: 0}
    });
    const [isSettingSchedule, setIsSettingSchedule] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });


    // Fetch existing blinking schedule on mount
    React.useEffect(() => {
        const fetchBlinkingSchedule = async () => {
            const blinking = await getBlinkingSchedule(layoutId);
            console.log('Fetched blinking schedule:', blinking);
            if (blinking) {
                setBlinkingScheduleState({
                    from: { hh: blinking.from.hh, mm: blinking.from.mm },
                    to: { hh: blinking.to.hh, mm: blinking.to.mm }
                });
            }
        };
        fetchBlinkingSchedule();
    }, [layoutId]);

    const handleTimeChange = (type, field, value) => {
        // Ensure value in valid from < to
        if (type === 'from' && field === 'hh' && parseInt(value) > blinkingSchedule.to.hh) {
            return;
        }
        if (type === 'from' && field === 'mm' && parseInt(value) > blinkingSchedule.to.mm && blinkingSchedule.from.hh === blinkingSchedule.to.hh) {
            return;
        }
        if (type === 'to' && field === 'hh' && parseInt(value) < blinkingSchedule.from.hh) {
            return;
        }
        if (type === 'to' && field === 'mm' && parseInt(value) < blinkingSchedule.from.mm && blinkingSchedule.to.hh === blinkingSchedule.from.hh) {
            return;
        }
        if(type === 'from' && field === 'hh' && parseInt(value) === blinkingSchedule.to.hh && blinkingSchedule.from.mm > blinkingSchedule.to.mm) {
            return;
        }
        setBlinkingScheduleState(prev => ({
            ...prev,
            [type]: {
                ...prev[type],
                [field]: parseInt(value) || 0
            }
        }));
    };

    const handleSaveSchedule = async () => {
        setIsSettingSchedule(true);
        try {
            await setBlinkingSchedule(layoutId, blinkingSchedule.from, blinkingSchedule.to);
            Alert.success('Blinking schedule set successfully');
        } catch (error) {
            console.error('Error setting blinking schedule:', error);
        } finally {
            setIsSettingSchedule(false);
        }
    };

    const handleChangeActiveLine = async (line) => {
        try {
            await changeActiveLine(line._id);
            setSnackbar({ open: true, message: `Line ${line.name} active changed successfully`, severity: 'success' });
            setLines(prev =>
                prev.map(l =>
                    l._id === line._id ? { ...l, isActive: !l.isActive, status: { ...l.status, signal: !l.isActive ? 'red' : 'off' } } : l,
                )
            );
        } catch (error) {
            setSnackbar({ open: true, message: 'Error changing line active', severity: 'error' });
            console.error('Error changing line active:', error);
        }
    };

    return (
        <div className={styles.tabContent}>
            {/* Blinking Schedule Section */}
            <div className={styles.detailSection}>
                <div className={styles.blinkingScheduleHeader}>
                    <h3 className={styles.detailSectionTitle}>
                        🟡 General Blinking Schedule
                    </h3>
                    <button className={styles.nowButton} onClick={onAllBlinking} title='Set all lines to blinking'>NOW</button>
                </div>
                <div className={styles.blinkingSchedule}>
                    <p className={styles.blinkingInfo}>
                        Set general blinking schedule for the entire layout
                    </p>
                    
                    <div className={styles.scheduleForm}>
                        <div className={styles.timeInputs}>
                            <input
                                type="number"
                                min="0"
                                max={blinkingSchedule.to.hh}
                                value={blinkingSchedule.from.hh}
                                onChange={(e) => handleTimeChange('from', 'hh', e.target.value)}
                                className={styles.timeInput}
                                placeholder="HH"
                            />
                            <span className={styles.timeSeparator}>:</span>
                            <input
                                type="number"
                                min="0"
                                max="59"
                                value={blinkingSchedule.from.mm}
                                onChange={(e) => handleTimeChange('from', 'mm', e.target.value)}
                                className={styles.timeInput}
                                placeholder="MM"
                            />
                        </div>
                        
                        <label className={styles.timeLabel}> to </label>
                        <div className={styles.timeInputs}>
                            <input
                                type="number"
                                min="0"
                                max="23"
                                value={blinkingSchedule.to.hh}
                                onChange={(e) => handleTimeChange('to', 'hh', e.target.value)}
                                className={styles.timeInput}
                                placeholder="HH"
                            />
                            <span className={styles.timeSeparator}>:</span>
                            <input
                                type="number"
                                min="0"
                                max="59"
                                value={blinkingSchedule.to.mm}
                                onChange={(e) => handleTimeChange('to', 'mm', e.target.value)}
                                className={styles.timeInput}
                                placeholder="MM"
                            />
                        </div>
                        
                        <button 
                            className={styles.setBlinkingButton}
                            onClick={handleSaveSchedule}
                            disabled={isSettingSchedule}
                        >
                            {isSettingSchedule ? (
                                <>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={styles.spinner}>
                                        <circle cx="12" cy="12" r="10"/>
                                        <polyline points="12,6 12,12 16,14"/>
                                    </svg>
                                </>
                            ) : (
                                <>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                                        <polyline points="17,21 17,13 7,13 7,21"/>
                                        <polyline points="7,3 7,8 15,8"/>
                                    </svg>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Lines Section */}
            <div className={styles.detailSection}>
                <div className={styles.detailSectionHeader}>
                    <h3 className={styles.detailSectionTitle}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="9,11 12,14 22,4"/>
                            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
                        </svg>
                        Traffic Lines ({lines.length})
                    </h3>
                    <button 
                        className={styles.addLineButton}
                        onClick={onAddLine}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="12" y1="5" x2="12" y2="19"/>
                            <line x1="5" y1="12" x2="19" y2="12"/>
                        </svg>
                        Add Line
                    </button>
                </div>

                {lines.length > 0 ? (
                    <div className={styles.linesList}>
                        <p className={styles.blinkingInfo}>
                            Click to 🟢 to change signal.
                        </p>
                        {lines.map((line) => (
                            <div key={line._id} className={styles.lineItem}
                                    id={`line-${line._id}`}
                                    onClick={() => onLineSelect(line)}
                                    onMouseEnter={() => onLineSelect(line)}
                                    title={line.description}
                            >
                                <div className={styles.lineItemHeader}>
                                    <div className={styles.lineItemInfo}>
                                        <h4 className={styles.lineItemName}>{line.name}</h4>
                                        <button className={`${styles.lineItemStatus} ${line.isActive ? styles.active : styles.inactive}`}
                                            title={'Click to change'}
                                            onClick={() => handleChangeActiveLine(line)}
                                            >
                                            {line.isActive ? 'Active' : 'Inactive'}
                                        </button>
                                    </div>
                                    <div className={styles.lineItemActions}>
                                        <button 
                                            className={styles.lineEditButton}
                                            onClick={() => onEditLine(line)}
                                            title="Edit"
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                            </svg>
                                        </button>
                                        <button 
                                            className={styles.lineDeleteButton}
                                            onClick={() => onDeleteLine(line)}
                                            title="Delete"
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M3 6h18"/>
                                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>
                                                <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                                <p className={styles.blinkingInfo} style={{paddingLeft: '12px'}}>
                                    {line.description || 'No description provided'}
                                </p>
                                <div className={styles.lineItemDetails}>
                                    <div className={styles.lineItemSection}>
                                        <span className={styles.lineItemLabel}>Direction:</span>
                                        <div className={styles.lineItemDirections}>
                                            {line.direction && line.direction.length > 0 ? (
                                                line.direction.map((dir, index) => (
                                                    <span key={index} className={styles.lineItemDirection}>
                                                        {dir === 'straight' ? '↑' : dir === 'left' ? '←' : dir === 'right' ? '→' : '•'}
                                                    </span>
                                                ))
                                            ) : (
                                                <span className={styles.lineItemEmpty}>-</span>
                                            )}
                                        </div>
                                    </div>
                                    
                                    <div className={styles.lineItemSection}>
                                        <span className={styles.lineItemLabel}>Duration:</span>
                                        <div className={styles.lineItemDurations}>
                                            <span className={styles.lineItemDuration}>
                                                <span className={styles.lineItemDurationLabel}>🟢</span>
                                                <span className={styles.lineItemDurationValue}>{line.greenDuration || 30}s</span>
                                            </span>
                                            <span className={styles.lineItemDuration}>
                                                <span className={styles.lineItemDurationLabel}>🟡</span>
                                                <span className={styles.lineItemDurationValue}>{line.yellowDuration || 5}s</span>
                                            </span>
                                        </div>
                                    </div>
                                    
                                    <div className={styles.lineItemSection}>
                                        <span className={styles.lineItemLabel}>Signal:</span>
                                        <div className={styles.lineItemSignals}>
                                            <button 
                                                className={`${styles.lineItemSignal} ${styles.green} ${line.status?.signal === 'green' ? styles.greenActive : ''}`}
                                                onClick={() => onSignalChange(line._id, 'green')}
                                                title='On green status of this line'
                                                disabled={loading}
                                            >
                                                🟢
                                            </button>
                                            <button 
                                                className={`${styles.lineItemSignal} ${styles.yellow} ${line.status?.signal === 'yellow' ? styles.yellowActive : ''}`}
                                                // onClick={() => onSignalChange(line._id, 'yellow')}
                                                disabled={loading}
                                            >
                                                🟡
                                            </button>
                                            <button 
                                                className={`${styles.lineItemSignal} ${styles.red} ${line.status?.signal === 'red' ? styles.redActive : ''}`}
                                                // onClick={() => onSignalChange(line._id, 'red')}
                                                disabled={loading}
                                            >
                                                🔴
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className={styles.noLines}>
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                            <polyline points="9,11 12,14 22,4"/>
                            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
                        </svg>
                        <p>No traffic lines in this layout</p>
                        <button className={styles.addFirstLine} onClick={onAddLine}>
                            Add First Line
                        </button>
                    </div>
                )}
            </div>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                message={snackbar.message}
            />
        </div>
    );
};

export default DetailTab; 